import { Duration } from 'luxon';
import { IMenuItem } from '../components/Common';

type Primitive = string | number | boolean | bigint | symbol | null | undefined;

export const retIfChanged = <T extends Primitive>(value: T, initialValue: T): T | undefined => {
    return value !== initialValue ? value : undefined;
};

export const handleNullInput = (value: string | null | undefined) => {
    return value ?? '';
};

export const removeUndefined = (object: any) => {
    return Object.entries(object)
        .filter(([_, value]) => value !== undefined)
        .reduce((obj, [key, value]) => ({ ...obj, [key]: value }), {});
};

export const shallowCompare = (obj1: any, obj2: any): boolean => {
    if (obj1 === obj2) {
        return true;
    }

    if (typeof obj1 !== 'object' || obj1 === null || typeof obj2 !== 'object' || obj2 === null) {
        return false;
    }

    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);

    if (keys1.length !== keys2.length) {
        return false;
    }

    for (const key of keys1) {
        if (obj1[key] !== obj2[key] || !obj2.hasOwnProperty(key)) {
            return false;
        }
    }

    return true;
};

export const toIntDefault0 = (str: string): number => {
    const num = parseInt(str, 10);
    if (isNaN(num)) return 0;
    return num;
};

export const secondsToFormat = (seconds: number, format: string): string => {
    const duration = Duration.fromObject({ seconds });
    return duration.toFormat(format);
};

export const upperSnakeCaseToStr = (snake: string): string => {
    return snake
        .toLowerCase()
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
};

interface IdentifiableWithName {
    id: string;
    name: string;
}

export const identifiableWithNameToIMenuItem = (obj: IdentifiableWithName, color?: string): IMenuItem => {
    return { label: obj.name, value: obj.id, color };
};

export const objectsAreEqual = (obj1: any, obj2: any) => {
    return JSON.stringify(obj1) === JSON.stringify(obj2);
};

export const returnOrFallback = <T>(obj: T | undefined, fallback: T): T => {
    return !obj ? fallback : obj;
};

export const secondIndexOf = (str: string, search: string): number => {
    const firstIndex = str.indexOf(search);
    return str.indexOf(search, firstIndex + 1);
};

const getFileExtension = (fileName: string): string => {
    return fileName.slice(fileName.lastIndexOf('.') + 1);
};

export const parseDocumentName = (fullName: string, maxLength: number): string => {
    fullName = decodeURIComponent(fullName);
    const secondIndex = secondIndexOf(fullName, '_');
    const parsedName = fullName.substring(secondIndex + 1);

    if (parsedName.length <= maxLength) return parsedName;

    const extension = getFileExtension(parsedName);
    const lastVisibleIndex = maxLength - extension.length - 3;

    if (lastVisibleIndex < 1) return parsedName.substring(0, maxLength - 2) + '...';

    const visibleName = parsedName.substring(0, lastVisibleIndex);
    return visibleName + '...' + extension;
};

export const getChangedValues = <T>(initialValues: T, submittedValues: T): Partial<T> => {
    const changedValues: Partial<T> = {};
    for (const key in submittedValues) {
        if (submittedValues[key] !== initialValues[key]) {
            changedValues[key] = submittedValues[key];
        }
    }
    return changedValues;
};

export const getCurrencySymbol = (currency: string): string => {
    switch (currency.toUpperCase()) {
        case 'EUR':
            return '€';
        case 'USD':
            return '$';
        case 'RSD':
            return 'RSD';
        default:
            return '$';
    }
};
